import styled from 'styled-components';

export const EditIndicator = styled.div`
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  opacity: 0;
  transform: translateX(4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  pointer-events: none;
`;

export const Div = styled.div<{ $itemHeight: number }>`
  outline: none;
  background: transparent;
  position: relative;
  height: ${({ $itemHeight }) => $itemHeight}px;

  &:hover,
  &:focus-within {
    background: ${({ theme }) => theme.colors.cultured};
  }

  &:hover ${EditIndicator} {
    opacity: 1;
    transform: translateX(0);
  }

  &:focus-within {
    outline: 1px solid ${({ theme }) => theme.colors.stroke};
  }

  &:focus-within ${EditIndicator} {
    opacity: 1;
    transform: translateX(0);
  }

  display: flex;
  align-items: center;

  tr[data-expanded='true'] & {
    margin-bottom: 1px;
  }

  .cydea-select {
    min-width: 0 !important;
    width: 100% !important;
    height: 100%;
  }
  .cydea-select__control {
    height: 100% !important;
    min-height: unset !important;
    border: none !important;
    background: transparent !important;
  }
  .cydea-select__value-container {
    padding: 0 14px !important;
  }
  .cydea-select__dropdown-indicator {
    display: none;
  }

  /*
   * CRPD-7358 - a menu that flips above the cell has to sit as tight to it as one that opens below.
   *
   * It did not. react-select puts an 8px gutter on BOTH sides of the menu; the shared select
   * stylesheet zeroes only margin-top, so the gutter survived underneath and opened a visible gap
   * above the cell that had no counterpart below it. The same stylesheet then nudges every menu up
   * 2px, which reads as "attached" below the cell and adds to the gap above it - so the two
   * directions were 10px apart. Both are cancelled here and the menu is flush either way.
   *
   * The 2px that is left is not a gap you can see - it is the breathing room react-select measures
   * with. It subtracts the menu's bottom margin when it works out how much of the screen the list
   * may use, and knows nothing about the 1px border the menu carries on each side, so at zero the
   * menu ended up 2px past the bottom of the window.
   *
   * Doubled selector: the rules being overridden are class-descendants too, so equal specificity
   * would leave this decided by whichever stylesheet styled-components happened to inject last.
   */
  && .cydea-select__menu {
    margin: 2px 0;
    transform: none;
  }

  /*
   * CRPD-7358 - the app's standard scrollbar, on the menu's own option list.
   *
   * Once the list is allowed to be shorter than its contents it can scroll, and which scrollbar it
   * drew depended on the table around it: GlobalStyles applies the house scrollbar to everything
   * INSIDE a .styled-scroll element, GenericTable's wrapper carries that class and TierTable's does
   * not, so the same dropdown came out thin and blue on Risk Management and a default grey slab on
   * Control Maturity. Same values as GlobalStyles' .styled-scroll, applied to the list itself so it
   * no longer matters what it happens to be nested in.
   *
   * The !important matters and is not cargo-culted from the rule this copies. state/theme/theme.tsx
   * injects a sitewide fallback scrollbar into <head> on every load, selected as
   * *:not(#main):not(.unstyled-scroll) - and :not(#main) carries ID specificity, so that fallback
   * outranks any selector built from classes alone, however many. GlobalStyles wins it with
   * !important; without the same qualifier here, the fallback would repaint the thumb in the
   * near-white stroke colour on exactly the page this block exists to fix, since nothing on
   * Control Maturity carries .styled-scroll to shield it.
   *
   * NB no backticks in this comment: it lives inside a styled-components template literal.
   */
  .cydea-select__menu-list {
    &::-webkit-scrollbar {
      width: 4px !important;
      height: 4px !important;
    }
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0) !important;
    }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.colors.cflowerBlue} !important;
    }
  }

  input,
  textarea,
  .MuiInputBase-root {
    background: transparent !important;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: ${({ $itemHeight }) => $itemHeight * 0.8}px;
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    height: ${({ $itemHeight }) => $itemHeight * 0.6}px;
  }
`;
